import React, { useState, useRef, useEffect, useCallback } from 'react';

const LineGrouped = ({
  initialX1 = 50,
  initialX2 = 250,
  containerWidth = 500,
  containerHeight = 100,
  circleDiameter = 10,
  lineHeight = 4,
  fixedYPercent = 50,
  minLineWidth = 30,
  lineColor = 'bg-[#929FC0]',
  circleColor = 'bg-[#929FC0]',
  containerBorderColor = 'border-gray-300',
  focusRingColor = 'focus:ring-blue-500',
  storageKey, // New prop for localStorage key
}) => {
  const getInitialValue = (key, defaultValue) => {
    if (!storageKey) return defaultValue;
    try {
      const storedValue = localStorage.getItem(`${storageKey}_${key}`);
      if (storedValue !== null) {
        const parsedValue = parseFloat(storedValue);
        // Basic validation: ensure it's a number and within reasonable initial bounds
        // More robust validation might be needed depending on how containerWidth can change
        if (!isNaN(parsedValue)) {
          return parsedValue;
        }
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }
    return defaultValue;
  };

  const [x1, setX1] = useState(() => getInitialValue('x1', initialX1));
  const [x2, setX2] = useState(() => getInitialValue('x2', initialX2));

  const [draggingCircle, setDraggingCircle] = useState(null);
  const [draggingLine, setDraggingLine] = useState(false);
  const [dragStartInfo, setDragStartInfo] = useState({ screenX: 0, initialX: 0, initialX1: 0, initialX2: 0 });

  const containerRef = useRef(null);
  const fixedY = (containerHeight * fixedYPercent) / 100;

  // Effect to save to localStorage when x1 or x2 change
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`${storageKey}_x1`, x1.toString());
        localStorage.setItem(`${storageKey}_x2`, x2.toString());
      } catch (error) {
        console.error("Error writing to localStorage:", error);
      }
    }
  }, [x1, x2, storageKey]);

  // Effect to adjust positions based on props and ensure minLineWidth
  // This will run after initial state is set (potentially from localStorage)
  useEffect(() => {
    let adjustedX1 = x1; // Start with current state (could be from localStorage or initial prop)
    let adjustedX2 = x2;

    // Clamp to container first
    const minPos = 0;
    const maxPos = containerWidth - circleDiameter;
    adjustedX1 = Math.max(minPos, Math.min(adjustedX1, maxPos));
    adjustedX2 = Math.max(minPos, Math.min(adjustedX2, maxPos));
    
    // Ensure minLineWidth
    if (Math.abs(adjustedX1 - adjustedX2) < minLineWidth) {
      if (adjustedX1 < adjustedX2) {
        adjustedX2 = Math.min(maxPos, adjustedX1 + minLineWidth);
        // If pushing x2 out of bounds, pull x1 back
        if (adjustedX2 === maxPos) {
            adjustedX1 = Math.max(minPos, adjustedX2 - minLineWidth);
        }
      } else { // x2 <= x1
        adjustedX1 = Math.min(maxPos, adjustedX2 + minLineWidth);
         // If pushing x1 out of bounds, pull x2 back
        if (adjustedX1 === maxPos) {
            adjustedX2 = Math.max(minPos, adjustedX1 - minLineWidth);
        }
      }
    }

    // Only update state if adjustments were made
    if (adjustedX1 !== x1) setX1(adjustedX1);
    if (adjustedX2 !== x2) setX2(adjustedX2);

  }, [containerWidth, circleDiameter, minLineWidth, initialX1, initialX2, x1, x2]); // x1, x2 added to re-evaluate if they change externally

  const getDynamicStyles = useCallback(() => {
    const lineRenderX1 = Math.min(x1, x2);
    const lineRenderX2 = Math.max(x1, x2);
    const lineStartPoint = lineRenderX1 + circleDiameter / 2;
    const lineEndPoint = lineRenderX2 + circleDiameter / 2;
    const visualLineWidth = lineEndPoint - lineStartPoint;

    return {
      circle1Style: { left: `${x1}px`, top: `${fixedY - circleDiameter / 2}px`, width: `${circleDiameter}px`, height: `${circleDiameter}px` },
      circle2Style: { left: `${x2}px`, top: `${fixedY - circleDiameter / 2}px`, width: `${circleDiameter}px`, height: `${circleDiameter}px` },
      lineStyle: { left: `${lineStartPoint}px`, top: `${fixedY - lineHeight / 2}px`, width: `${visualLineWidth}px`, height: `${lineHeight}px` },
    };
  }, [x1, x2, circleDiameter, lineHeight, fixedY]);

  const styles = getDynamicStyles();

  const handleMouseDownOnCircle = (event, circleId) => {
    if (event.button !== 0) return;
    setDraggingCircle(circleId);
    setDragStartInfo({ screenX: event.screenX, initialX: circleId === 'circle1' ? x1 : x2, initialX1: x1, initialX2: x2 });
    event.preventDefault();
  };

  const handleMouseDownOnLine = (event) => {
    if (event.button !== 0) return;
    setDraggingLine(true);
    setDragStartInfo({ screenX: event.screenX, initialX1: x1, initialX2: x2 });
    event.preventDefault();
  };

  const handleMouseUp = useCallback(() => {
    setDraggingCircle(null);
    setDraggingLine(false);
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (!draggingCircle && !draggingLine) return;
    const deltaX = event.screenX - dragStartInfo.screenX;

    if (draggingCircle) {
      let newPositionForCircle = dragStartInfo.initialX + deltaX;
      const minContainerBound = 0;
      const maxContainerBound = containerWidth - circleDiameter;
      newPositionForCircle = Math.max(minContainerBound, Math.min(newPositionForCircle, maxContainerBound));

      if (draggingCircle === 'circle1') {
        const maxAllowedX1 = x2 - minLineWidth - circleDiameter;
        if (newPositionForCircle > maxAllowedX1) newPositionForCircle = maxAllowedX1;
        setX1(Math.max(minContainerBound, newPositionForCircle));
      } else {
        const minAllowedX2 = x1 + minLineWidth + circleDiameter;
        if (newPositionForCircle < minAllowedX2) newPositionForCircle = minAllowedX2;
        setX2(Math.min(maxContainerBound, newPositionForCircle));
      }
    } else if (draggingLine) {
      const proposedX1 = dragStartInfo.initialX1 + deltaX;
      const proposedX2 = dragStartInfo.initialX2 + deltaX;
      const componentProposedLeft = Math.min(proposedX1, proposedX2);
      const componentProposedRight = Math.max(proposedX1, proposedX2) + circleDiameter;
      let finalDeltaX = deltaX;
      if (componentProposedLeft < 0) finalDeltaX = deltaX - componentProposedLeft;
      else if (componentProposedRight > containerWidth) finalDeltaX = deltaX - (componentProposedRight - containerWidth);
      setX1(dragStartInfo.initialX1 + finalDeltaX);
      setX2(dragStartInfo.initialX2 + finalDeltaX);
    }
  }, [draggingCircle, draggingLine, dragStartInfo, containerWidth, circleDiameter, minLineWidth, x1, x2]);

  useEffect(() => {
    if (draggingCircle || draggingLine) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingCircle, draggingLine, handleMouseMove, handleMouseUp]);

  const circleBaseClasses = `absolute rounded-full ${circleColor} focus:outline-none focus:ring-2 ${focusRingColor} focus:ring-offset-2 z-10 cursor-ew-resize`;
  const lineBaseClasses = `absolute ${lineColor} ${draggingLine ? 'cursor-grabbing' : 'cursor-grab'}`;

  if (containerWidth <= 0 || containerHeight <=0) return null; // Don't render if container has no dimensions

  return (
    <div
      ref={containerRef}
      className={`relative select-none touch-none ${containerBorderColor !== 'border-transparent' ? `border ${containerBorderColor}` : ''}`}
      style={{ width: `${containerWidth}px`, height: `${containerHeight}px` }}
    >
      <div className={circleBaseClasses} style={styles.circle1Style} onMouseDown={(e) => handleMouseDownOnCircle(e, 'circle1')} role="slider" aria-valuenow={x1} aria-label="Start point" tabIndex={0}/>
      <div className={circleBaseClasses} style={styles.circle2Style} onMouseDown={(e) => handleMouseDownOnCircle(e, 'circle2')} role="slider" aria-valuenow={x2} aria-label="End point" tabIndex={0}/>
      <div className={lineBaseClasses} style={styles.lineStyle} onMouseDown={handleMouseDownOnLine}/>
    </div>
  );
};

export default LineGrouped;